import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
// Imported rather than reached for on window: turbo-rails does set the
// global, but a controller that depends on load order is a controller that
// breaks whenever the order changes.
import * as Turbo from "@hotwired/turbo"

// Drag a planned meal to another slot, or another day.
//
// Not the sortable controller, which reorders within one list. This MOVES
// between containers, and the difference is not cosmetic: a reorder is a DOM
// change that saves with the form, where a move is a server round-trip that
// can fail — so this one has to say where a meal landed, wait, and put it
// back if the answer never comes.
//
// One controller for the whole grid rather than one per slot: SortableJS
// needs every list in a group to be created, and a controller per slot would
// be twenty-eight instances all declaring the same group.
export default class extends Controller {
  static values = { url: String }

  connect() {
    // The slot under the pointer right now, or null if it is over anything
    // else — the header, the gap between columns, off the grid entirely.
    // elementsFromPoint rather than event.target: the drag mirror follows
    // the cursor and is what the event lands on, so target is always the
    // item being dragged.
    this.onPointer = (event) => {
      const x = event.clientX, y = event.clientY
      if (x === undefined) return

      const under = document.elementsFromPoint(x, y)

      this.overSlot = under
        .map((node) => node.closest?.("[data-meal-slot]"))
        .find(Boolean) || null

      // The day the drag began in stays expanded until the pointer leaves
      // it, then collapses like everything else. Checked from the real
      // position rather than :hover, which a drag freezes — see holdSource.
      //
      // Only the week grid expands days; the day view shows one day at full
      // size already, so there is nothing to hold open and `source` is never
      // set there.
      const day = under.map((node) => node.closest?.(".week-grid > *")).find(Boolean)
      if (this.source && day !== this.source) this.releaseSource()
    }

    this.attach()

    // A day re-renders whenever a meal is added or removed, which destroys
    // its slots and builds new ones — and a Sortable bound to an element
    // that is no longer in the document is a slot you cannot drop into.
    // Re-scanning after each render is what keeps the whole week draggable
    // rather than only the days nothing has happened to.
    //
    // IT MUST RUN AFTER THE MORPH, which is where this went wrong for a
    // long time. The old version queued attach() in a requestAnimationFrame
    // from turbo:before-stream-render — that fires BEFORE the stream is
    // applied, so it bound the slots about to be discarded and left the
    // ones on the page bound to nothing. Every count looked healthy:
    // seven slots really were bound, they were the wrong seven, and the
    // only way to see it was comparing the bound elements against the live
    // ones by identity.
    //
    // turbo:morph-element IS THE EVENT, and the name is the whole lesson.
    // turbo:morph does NOT fire for a stream that morphs a target — only
    // the per-element one does, so listening for turbo:morph rescanned
    // never.
    //
    // It fires once per morphed element, several hundred times for one day,
    // so the burst is coalesced into a single rescan on the next tick —
    // by which point the DOM has settled. turbo:render and
    // turbo:frame-render cover the paths that do not morph at all.
    this.onRender = () => {
      clearTimeout(this.rescan)
      this.rescan = setTimeout(() => this.attach(), 0)
    }
    document.addEventListener("turbo:morph-element", this.onRender)
    document.addEventListener("turbo:render", this.onRender)
    document.addEventListener("turbo:frame-render", this.onRender)
  }

  attach() {
    // Whatever is bound now is either stale or about to be rebound.
    this.sortables?.forEach((sortable) => sortable.destroy())

    const slots = Array.from(this.element.querySelectorAll("[data-meal-slot]"))

    // DESTROY BY ASKING THE DOM, not only by trusting the array above.
    //
    // this.sortables holds the instances we made last time. After a morph
    // those instances point at elements that may have been replaced, so
    // destroy() on them does not necessarily clear the instance attached to
    // the element that is on the page NOW — and Sortable.create on an
    // already-bound element stacks a second set of handlers rather than
    // replacing the first. Two instances on one slot fight, and neither
    // starts a drag.
    slots.forEach((slot) => Sortable.get?.(slot)?.destroy())
    this.sortables = slots.map((slot) => Sortable.create(slot, {
      group: "planned-meals",
      animation: 150,
      draggable: "[data-planned-meal-id]",
      ghostClass: "is-drop-target",
      chosenClass: "is-grabbed",
      dragClass: "is-dragging",
      // A long press before a drag on touch, so scrolling the week still
      // works — without it every swipe starting on a meal drags it.
      delay: 200,
      delayOnTouchOnly: true,
      // NOTHING SELECTS DURING THAT PRESS — and the fix for that is CSS,
      // not a Sortable option. iOS starts a text selection on a long press,
      // which is exactly the gesture this waits 200ms for, so the selection
      // began, the callout appeared, and the drag never got its pointer.
      // Cody, 14 Sep. See the user-select rule on [data-planned-meal-id].
      //
      // Three options were set here first and all three were dead:
      // preventOnFilter and forceFallback are Sortable's own defaults, and
      // preventOnFilter needs a `filter` selector this instance does not
      // have. supportPointer is likewise already true on iOS — the
      // library's default is `'PointerEvent' in window && (!Safari || IOS)`,
      // and the IOS arm is what makes it apply there.
      // An empty slot has no height to aim at, so the grid opens them up for
      // the duration of the drag and closes them again afterwards.
      onStart: (event) => {
        this.element.classList.add("is-dragging-meal")
        this.overSlot = null

        // Hold the day the drag began in open.
        //
        // Reordering happens inside one day, and it is much easier at full
        // width — the rows are bigger and the slots further apart. Every
        // OTHER day stays collapsed, so nothing shifts under the cursor
        // while you are aiming: an expansion swaps a row of figures for a
        // ring and three bars, and everything below it moves down.
        //
        // Marked rather than left to :hover because a drag captures the
        // pointer: hover freezes wherever it was and never updates, so it
        // cannot say when you have left.
        this.source = event.from.closest(".week-grid > *")
        this.source?.classList.add("is-drag-source")
        // Where the pointer actually is, which is not what Sortable reports.
        //
        // Sortable REMEMBERS the last list the pointer crossed and commits
        // to it on release, wherever the release happened — so dragging out
        // over a slot and letting go on the day header dropped the meal into
        // that slot, at a spot nobody chose. Tracking the pointer ourselves
        // is what lets a drop outside every slot be treated as a cancel.
        document.addEventListener("dragover", this.onPointer, true)
        document.addEventListener("pointermove", this.onPointer, true)
      },
      onMove: (event) => {
        this.element.querySelectorAll(".is-over")
          .forEach((node) => node.classList.remove("is-over"))
        event.to?.classList.add("is-over")
      },
      onEnd: (event) => {
        document.removeEventListener("dragover", this.onPointer, true)
        document.removeEventListener("pointermove", this.onPointer, true)

        this.element.querySelectorAll(".is-over")
          .forEach((node) => node.classList.remove("is-over"))

        // Held until the next real pointer move — see releaseDrag.
        this.releaseDrag()

        // Released over nothing that accepts a meal. Sortable has already
        // moved it into whichever slot the pointer last crossed, so this is
        // a cancel that has to be undone, not a no-op.
        if (!this.overSlot) return this.revert(event)

        this.dropped(event)
      }
    }))
  }

  // Collapse the day the drag began in. Once left, it is just another day.
  releaseSource() {
    this.source?.classList.remove("is-drag-source")
    this.source = null
  }

  // Hand the week back to :hover, once :hover is telling the truth again.
  //
  // At the drop the browser has not re-evaluated :hover — a drag captures
  // the pointer, so it is still matching whatever it matched when the drag
  // began, and it will not recompute until the pointer next moves. On a
  // reorder within one day that is usually not at all.
  //
  // So the drag state is held until that move, with .is-drag-source keeping
  // the source day open in the meantime. That is why there is no timeout
  // fallback: waiting it out would collapse the day the pointer is sitting
  // on, which is the thing this exists to prevent. Holding is a correct
  // resting state, not a stopgap.
  releaseDrag() {
    const release = () => {
      document.removeEventListener("pointermove", release, true)
      this.element.classList.remove("is-dragging-meal")

      // Queued, so the source is let go only after the browser has
      // recomputed :hover — dropping both in one frame flickered.
      requestAnimationFrame(() => this.releaseSource())
    }

    document.addEventListener("pointermove", release, true)
  }

  disconnect() {
    this.releaseSource()
    document.removeEventListener("dragover", this.onPointer, true)
    document.removeEventListener("pointermove", this.onPointer, true)
    clearTimeout(this.rescan)
    document.removeEventListener("turbo:morph-element", this.onRender)
    document.removeEventListener("turbo:render", this.onRender)
    document.removeEventListener("turbo:frame-render", this.onRender)
    this.sortables?.forEach((sortable) => sortable.destroy())
    this.sortables = null
  }

  async dropped(event) {
    const slot = event.to
    const from = event.from

    // Dropped exactly where it started — same slot, same index. Sortable
    // fires onEnd either way, and a round trip to tell the server nothing
    // changed is a round trip that can fail for no reason.
    if (slot === from && event.newIndex === event.oldIndex) return

    const id = event.item.dataset.plannedMealId
    // A row with no planned meal behind it is something logged that was
    // never planned. There is no plan to move, so it stays where it is —
    // reverted rather than silently left in the wrong slot, since the drop
    // has already moved it in the DOM.
    if (!id) return this.revert(event)

    const body = new FormData()

    // Which page is asking, so the server answers with streams this page
    // actually contains — the week grid and the day view share this
    // controller and have nothing in common in the DOM.

    body.set("planned_meal[date]", slot.dataset.mealSlotDate)
    body.set("planned_meal[meal_type]", slot.dataset.mealSlotType)
    if (slot.dataset.mealSlotPosition) {
      body.set("planned_meal[position]", slot.dataset.mealSlotPosition)
    }

    // Where in the slot it landed, so a reorder INSIDE one slot saves.
    //
    // Rank within a slot is its own column — see PlannedMeal#sort_order.
    // Before it existed, meals sharing a slot ordered by id, so dragging one
    // above another had nothing to write to: the drop looked right until the
    // next render put it back where it started.
    //
    // Counted over the slot's meals rather than taken from event.newIndex,
    // which counts every child — the add button among them.
    body.set("index", this.indexIn(slot, event.item))

    try {
      const response = await fetch(this.urlValue.replace(":id", id), {
        method: "PATCH",
        body,
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || ""
        }
      })

      if (!response.ok) return this.revert(event)

      // Figures only — the meal itself is already where it belongs, because
      // the drop put it there. The server used to re-render both days, which
      // threw that DOM away and rebuilt it: the fresh card started collapsed
      // and the hover expansion replayed from the top, so the day swiped shut
      // and open again under the cursor. See PlannedMealsController#moved?.
      Turbo.renderStreamMessage(await response.text())
    } catch {
      this.revert(event)
    }
  }

  // How many meals sit above this one in its slot.
  indexIn(slot, item) {
    return Array.from(slot.querySelectorAll("[data-planned-meal-id]")).indexOf(item)
  }

  // Put the meal back where it came from.
  //
  // The drop already moved it in the DOM — optimistically, so the drag feels
  // immediate — and a failed save leaves the screen claiming something that
  // did not happen. Better a meal that visibly snaps back than a plan that
  // silently disagrees with the server.
  revert(event) {
    const { from, item, oldIndex } = event

    // Take it out first.
    //
    // When the drop landed back in its own list the item is still a child of
    // `from`, so children[oldIndex] can BE the item — and insertBefore(item,
    // item) is a no-op that leaves it exactly where the failed drag put it.
    // Removing it makes oldIndex mean what it meant when the drag started:
    // the position in a list this item is not in.
    item.remove()

    // The container holds nothing but meals — the add button lives outside
    // it — so oldIndex and the child list agree, and insertBefore(item,
    // null) at the end is just appendChild.
    from.insertBefore(item, from.children[oldIndex] || null)
  }
}
